import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import siteConfig from '../data/siteConfig.json';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'glass-nav py-3' : 'py-5'
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3 group"
            data-testid="nav-logo"
          >
            <img
              src={siteConfig.brand.logo}
              alt="ROMIND"
              className="h-10 w-10 transition-transform duration-300 group-hover:scale-110"
            />
            <span className="text-xl font-semibold tracking-tight text-white hidden sm:block">
              {siteConfig.brand.name}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {siteConfig.navigation.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`px-4 py-2 text-sm font-medium transition-all duration-300 relative group ${
                  location.pathname === item.href
                    ? 'text-white'
                    : 'text-slate-400 hover:text-white'
                }`}
                data-testid={`nav-link-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                {item.label}
                <span
                  className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-violet-500 transition-all duration-300 ${
                    location.pathname === item.href ? 'w-6' : 'w-0 group-hover:w-6'
                  }`}
                  style={{
                    boxShadow: location.pathname === item.href ? '0 0 10px rgba(124, 58, 237, 0.8)' : 'none',
                  }}
                />
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <a
              href="#early-access"
              className="btn-primary text-sm"
              data-testid="nav-cta-button"
            >
              {siteConfig.cta.primary.text}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className="lg:hidden p-2 text-white"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ${
            isMobileOpen ? 'max-h-screen opacity-100 mt-4' : 'max-h-0 opacity-0'
          }`}
          data-testid="mobile-menu"
        >
          <div className="glass-panel p-4 space-y-2">
            {siteConfig.navigation.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`block px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === item.href
                    ? 'bg-violet-500/20 text-white'
                    : 'text-slate-400 hover:bg-white/5 hover:text-white'
                }`}
                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                {item.label}
              </Link>
            ))}
            <a
              href="#early-access"
              className="block btn-primary text-center mt-4"
              data-testid="mobile-cta-button"
            >
              {siteConfig.cta.primary.text}
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
